import { useCallback, useMemo } from 'react';

import { useLazyQuery } from '@apollo/client';
import { AdobeJobStatus } from '@prisma/client';
import type {
    GetAgreementStatus,
    GetAgreementStatusVariables,
    LatestAdobeSignJob,
} from 'web/types/graphql';

import { GET_AGREEMENT_STATUS } from 'src/graphql/queries/getAgreementStatus.query';
import { GET_LATEST_ADOBE_SIGN_JOB } from 'src/graphql/queries/merchantApplications.query';

import { UseAgreementStatusProps, UseAgreementStatusReturn } from './types';

function useAgreementStatus({
    applicationId,
    onAgreementStatusCompleted,
}: UseAgreementStatusProps): UseAgreementStatusReturn {
    const [
        fetchLatestJob,
        {
            data: latestJobData,
            refetch: refetchLatestJobData,
            loading: loadingLatestJob,
            error: errorLatestJob,
        },
    ] = useLazyQuery<LatestAdobeSignJob>(GET_LATEST_ADOBE_SIGN_JOB);

    const [
        getAgreementStatusQuery,
        {
            data: agreementStatusData,
            refetch: refetchAgreementStatus,
            loading: loadingAgreementStatus,
            error: errorAgreementStatus,
        },
    ] = useLazyQuery<GetAgreementStatus, GetAgreementStatusVariables>(GET_AGREEMENT_STATUS, {
        onCompleted: (data) => {
            const appId = data?.agreementStatus?.applicationId ?? applicationId;
            if (appId) fetchLatestJob({ variables: { applicationId: appId } });
            onAgreementStatusCompleted?.(data);
        },
    });

    const getAgreementStatus = useCallback(
        (variables?: { applicationId?: number }) => {
            const appId = variables?.applicationId ?? applicationId;
            if (!appId) return;
            getAgreementStatusQuery({ variables: { applicationId: appId } });
        },
        [applicationId, getAgreementStatusQuery]
    );

    const isAgreementSigned = useMemo(() => {
        return agreementStatusData?.agreementStatus?.status === AdobeJobStatus.SIGNED;
    }, [agreementStatusData]);

    const previewUrl = useMemo(() => {
        return agreementStatusData?.agreementStatus?.previewUrl ?? '';
    }, [agreementStatusData]);

    return {
        getAgreementStatus,
        refetchAgreementStatus,
        agreementStatusData,
        isAgreementSigned,
        previewUrl,
        latestJobData,
        refetchLatestJobData,
        loadingAgreementStatus,
        loadingLatestJob,
        errorAgreementStatus,
        errorLatestJob,
    };
}

export default useAgreementStatus;
